/**
 * useRealtimeCases — live OR board updates via Supabase Realtime.
 *
 * Subscribes to postgres_changes on cr.or_cases / cr.case_events /
 * cr.case_vitals (added to the supabase_realtime publication) and
 * invalidates the relevant react-query caches so the UI updates within
 * ~a second of a status change, instead of waiting for the 30s poll.
 *
 * Realtime respects RLS — subscribers only receive rows their org's
 * policies allow. Polling stays in place as a fallback transport.
 */

import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

export function useRealtimeCases(extraKeys: (string | number)[][] = []) {
    const qc = useQueryClient();

    useEffect(() => {
        const invalidate = () => {
            qc.invalidateQueries({ queryKey: ['active-cases'] });
            qc.invalidateQueries({ queryKey: ['active-cases-count'] });
            for (const key of extraKeys) qc.invalidateQueries({ queryKey: key });
        };

        const channel = supabase
            .channel('or-live-cases')
            .on('postgres_changes', { event: '*', schema: 'cr', table: 'or_cases' }, invalidate)
            .on('postgres_changes', { event: '*', schema: 'cr', table: 'case_events' }, invalidate)
            .on('postgres_changes', { event: '*', schema: 'cr', table: 'case_vitals' }, invalidate)
            .subscribe();

        return () => {
            supabase.removeChannel(channel);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [qc, JSON.stringify(extraKeys)]);
}
